import { Component, DestroyRef, EventEmitter, Input, OnChanges, Output, inject } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';
import { ClientSystemSettingValueModel, ClientSystemSettingViewModel, ListItem, SystemSettingType } from '../../core/api/models';
import { ClientsApiService } from '../../core/api/services';
import { Utils } from '../../core/utils';
import { AlertService } from '../../shared/services/alert.service';
import { Editor, editorConfig } from '../../shared/ckeditor/ckeditor-config';

interface SettingEditor {
  item: ClientSystemSettingValueModel;
  setting: ClientSystemSettingViewModel;
  kind: string;
  control: FormControl;
  options: ListItem[];
  error?: string;
  saving: boolean;
  inputID: string;
  scopeName: string;
  hasOwnValue: boolean;
  isReference: boolean;
  isDropdown: boolean;
  isFile: boolean;
  loadingOptions: boolean;
  optionsLoaded: boolean;
  optionRequest: number;
}

@Component({
  selector: 'app-client-system-settings',
  templateUrl: './client-system-settings.component.html',
  standalone: false
})
export class ClientSystemSettingsComponent implements OnChanges {
  private readonly destroyRef = inject(DestroyRef);
  private request = 0;
  @Input({ required: true }) clientID = 0;
  @Input({ required: true }) systemSettingID = 0;
  @Output() savingChange = new EventEmitter<boolean>();
  editors: SettingEditor[] = [];
  loading = false;
  saving = false;
  readonly Editor = Editor;
  readonly editorConfig = editorConfig;

  constructor(
    private readonly clientsApi: ClientsApiService,
    private readonly alertService: AlertService
  ) {}

  ngOnChanges(): void {
    ++this.request;
    this.saving = false;
    this.savingChange.emit(false);
    this.editors = [];
    this.load();
  }

  load(): void {
    if (!this.clientID || !this.systemSettingID || this.saving) return;
    const request = ++this.request;
    const clientID = this.clientID;
    const systemSettingID = this.systemSettingID;
    this.loading = true;
    this.editors = [];
    forkJoin({
      settings: this.clientsApi.GetClientSystemSettings({ clientID, body: { systemSettingID } }),
      companies: this.clientsApi.GetClientCompanies({ clientID })
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: data => {
        if (request !== this.request) return;
        this.loading = false;
        const setting = data.settings.find(item => item.SystemSetting === systemSettingID);
        if (!setting) {
          this.alertService.error('This setting is no longer available. Close the popup and refresh the settings list.');
          return;
        }
        const type = setting.SystemSettingType;
        if (type == null) {
          this.alertService.error('This setting has no storage type. Correct the definition before editing.');
          return;
        }
        const kind = SystemSettingType[type]?.toLowerCase() ?? '';
        const isReference = type >= 100 && type !== SystemSettingType.Photo;
        const isFile = ['bytes', 'file', 'image', 'video', 'audio', 'photo'].includes(kind);
        const defaultValue: ClientSystemSettingValueModel = { SystemSetting: setting.SystemSetting };
        if (kind === 'checkbox') defaultValue.BitValue = setting.DefaultValue ?? null;
        else if (['number', 'percentageinteger', 'oneoption'].includes(kind) || isReference)
          defaultValue.IntValue = setting.DefaultValue?.Value ?? setting.DefaultValue ?? null;
        else if (['decimal', 'currency', 'percentagedecimal'].includes(kind)) defaultValue.DecimalValue = setting.DefaultValue ?? null;
        else if (['date', 'time', 'datetime'].includes(kind)) defaultValue.DateValue = setting.DefaultValue ?? null;
        else if (kind !== 'password') defaultValue.Value = setting.DefaultValue == null ? null : String(setting.DefaultValue);
        const enterprise = setting.Values?.find(value => value.CompanyID == null) ?? defaultValue;
        const scopes = [
          { ID: null, Name: 'Enterprise' },
          ...data.companies.filter(company => company.ID != null).map(company => ({ ID: company.ID!, Name: company.BusinessName || company.BusinessAbbreviation || 'Company #' + company.ID }))
        ];
        this.editors = scopes.map(scope => {
          const ownValue = setting.Values?.find(value => (value.CompanyID ?? null) === scope.ID);
          const effectiveValue = ownValue ?? enterprise;
          const editor: SettingEditor = {
            item: { SystemSetting: setting.SystemSetting, CompanyID: scope.ID }, setting, kind, options: [], saving: false,
            inputID: 'setting-' + systemSettingID + '-' + (scope.ID ?? 'enterprise'),
            scopeName: scope.Name, control: new FormControl(), hasOwnValue: !!ownValue, isReference, isFile,
            isDropdown: [SystemSettingType.UserLoginPolicy, SystemSettingType.PayPeriodFrequency, SystemSettingType.ServiceSetting].includes(type),
            loadingOptions: false, optionsLoaded: false, optionRequest: 0
          };
          if (!kind) editor.error = 'This setting has an unsupported storage type.';
          if (setting.Required && kind !== 'checkbox' && !isFile)
            editor.control.addValidators(Validators.required);
          if (kind === 'password') {
            editor.control.removeValidators(Validators.required);
            if (setting.Required) editor.control.addValidators(control =>
              editor.item.BitValue === true && !control.dirty ? null : Validators.required(control));
          }
          if (type >= SystemSettingType.Text && type <= SystemSettingType.Color)
            editor.control.addValidators(Validators.maxLength(4000));
          if (kind === 'email') editor.control.addValidators(Validators.email);
          const options = setting.Settings ?? {};
          editor.options = options.Options ?? options.options ?? [];
          if (!Array.isArray(editor.options)) {
            editor.options = [];
            editor.error = 'The setting options are invalid. Correct the definition before editing.';
          }
          const min = options.Min ?? options.min;
          const max = options.Max ?? options.max;
          if (min != null) editor.control.addValidators(Validators.min(min));
          if (max != null) editor.control.addValidators(Validators.max(max));
          if (options.maxLength != null) editor.control.addValidators(Validators.maxLength(options.maxLength));
          this.setEditorValue(editor, effectiveValue);
          if (editor.error || scope.ID != null && !setting.ShowOnCompanySettings) editor.control.disable();
          return editor;
        });
      },
      error: error => {
        if (request !== this.request) return;
        this.loading = false;
        this.alertService.error(Utils.getAjaxError(error));
      }
    });
  }

  setEditorValue(editor: SettingEditor, item: ClientSystemSettingValueModel): void {
    editor.item = { ...item, CompanyID: editor.item.CompanyID, SystemSetting: editor.setting.SystemSetting };
    let value: unknown = item.Value ?? null;
    if (editor.kind === 'password') value = null;
    else if (editor.kind === 'checkbox') value = item.BitValue ?? null;
    else if (['number', 'percentageinteger', 'oneoption'].includes(editor.kind) || editor.isDropdown) value = item.IntValue ?? null;
    else if (editor.isReference) value = item.IntValue == null ? null : { Value: item.IntValue, Text: item.Value || '#' + item.IntValue };
    else if (['decimal', 'currency', 'percentagedecimal'].includes(editor.kind)) value = item.DecimalValue ?? null;
    else if (['date', 'time', 'datetime'].includes(editor.kind)) {
      const date = item.DateValue?.replace(/(?:Z|[+-]\d{2}:\d{2})$/, '') ?? '';
      value = !date ? null : editor.kind === 'date' ? date.slice(0, 10) : editor.kind === 'time' ? date.slice(11, 19) : date;
    } else if (editor.kind === 'multipleoptions' && editor.options.length) {
      const selected = (item.Value || '').split('\n').filter(Boolean).map(option => Number(option.split('|')[0]));
      if (selected.some(id => !Number.isInteger(id))) editor.error = 'The saved selection is invalid. Correct it before editing.';
      value = selected;
    }
    editor.control.reset(value);
    if (editor.isDropdown && item.IntValue != null && !editor.options.some(option => option.Value === item.IntValue))
      editor.options = [{ Value: item.IntValue, Text: item.Value || '#' + item.IntValue }, ...editor.options];
  }

  loadOptions(editor: SettingEditor, query = ''): void {
    if (editor.isDropdown && (editor.optionsLoaded || editor.loadingOptions)) return;
    const request = this.request;
    const optionRequest = ++editor.optionRequest;
    editor.loadingOptions = true;
    this.clientsApi.GetClientFormOptions({ clientID: this.clientID, body: {
      systemSettingTypeID: editor.setting.SystemSettingType, prefixText: query,
      count: editor.isDropdown ? 0 : 100, customSource: editor.setting.Settings?.Source
    } }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: options => {
        if (request !== this.request || optionRequest !== editor.optionRequest) return;
        const selected = editor.control.value;
        if (editor.isDropdown && selected != null && !options.some(option => option.Value === selected))
          options = [...options, ...editor.options.filter(option => option.Value === selected)];
        editor.options = options;
        editor.optionsLoaded = true;
        editor.loadingOptions = false;
      },
      error: error => {
        if (request !== this.request || optionRequest !== editor.optionRequest) return;
        editor.loadingOptions = false;
        this.alertService.error(Utils.getAjaxError(error));
      }
    });
  }

  save(editor: SettingEditor): void {
    const companyID = editor.item.CompanyID ?? null;
    if (this.saving || this.loading || editor.error || editor.isFile || !editor.control.dirty
      || (companyID !== null && !editor.setting.ShowOnCompanySettings)) return;
    editor.control.markAsTouched();
    if (editor.control.invalid) {
      this.alertService.error('Enter a valid value for ' + editor.setting.SettingName + '.');
      return;
    }
    const clientID = this.clientID;
    const request = this.request;
    const value = editor.control.value;
    const body: ClientSystemSettingValueModel = {
      SystemSetting: editor.setting.SystemSetting, CompanyID: companyID
    };
    if (editor.kind === 'checkbox') body.BitValue = value;
    else if (['number', 'percentageinteger', 'oneoption'].includes(editor.kind) || editor.isReference) {
      if (editor.isReference && !editor.isDropdown && value != null && !Number.isInteger(value.Value)) {
        this.alertService.error('Select an available option for ' + editor.setting.SettingName + '.');
        return;
      }
      const id = editor.isReference && !editor.isDropdown ? value?.Value : value;
      if (id != null && (!Number.isInteger(id) || id < -2147483648 || id > 2147483647)) {
        this.alertService.error('Enter a valid whole number for ' + editor.setting.SettingName + '.');
        return;
      }
      body.IntValue = id ?? null;
      body.Value = editor.isReference && !editor.isDropdown ? value?.Text : editor.options.find(option => option.Value === id)?.Text;
    } else if (['decimal', 'currency', 'percentagedecimal'].includes(editor.kind)) body.DecimalValue = value;
    // Settings use SQL datetime2 wall-clock values, not instants with a timezone.
    else if (['date', 'time', 'datetime'].includes(editor.kind)) body.DateValue = value ? (editor.kind === 'time' ? '1970-01-01T' : '') + value : null;
    else if (editor.kind === 'multipleoptions' && editor.options.length)
      body.Value = (value as number[]).map(id => id + '|' + (editor.options.find(option => option.Value === id)?.Text ?? '')).join('\n');
    else body.Value = value ?? null;
    this.setSaving(editor, true);
    this.clientsApi.SaveClientSystemSetting({ clientID, body }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        if (request !== this.request) return;
        this.setSaving(editor, false);
        const saved = editor.kind === 'password' ? { ...body, Value: null, BitValue: !!body.Value } : body;
        editor.hasOwnValue = true;
        this.setEditorValue(editor, saved);
        if (companyID === null) {
          for (const companyEditor of this.editors) {
            if (companyEditor.item.CompanyID != null && !companyEditor.hasOwnValue && !companyEditor.control.dirty)
              this.setEditorValue(companyEditor, saved);
          }
        }
        this.alertService.success(editor.setting.SettingName + ' saved successfully.');
      },
      error: error => {
        if (request !== this.request) return;
        this.setSaving(editor, false);
        this.alertService.error(Utils.getAjaxError(error));
      }
    });
  }

  setSaving(editor: SettingEditor, saving: boolean): void {
    editor.saving = this.saving = saving;
    for (const field of this.editors) {
      if (saving || field.error || field.item.CompanyID != null && !field.setting.ShowOnCompanySettings)
        field.control.disable({ emitEvent: false });
      else
        field.control.enable({ emitEvent: false });
    }
    this.savingChange.emit(saving);
  }

  upload(editor: SettingEditor, input: HTMLInputElement): void {
    const file = input.files?.[0];
    const companyID = editor.item.CompanyID ?? null;
    if (!file || this.saving || this.loading || !editor.item.SystemSetting || (companyID !== null && !editor.setting.ShowOnCompanySettings)) return;
    if (file.size === 0 || file.size > 50 * 1024 * 1024) {
      input.value = '';
      this.alertService.error('Select a non-empty file of at most 50 MB.');
      return;
    }
    const clientID = this.clientID;
    const request = this.request;
    this.setSaving(editor, true);
    this.clientsApi.UploadClientSystemSetting({
      clientID, body: { systemSettingID: editor.item.SystemSetting, companyID: companyID ?? undefined, file }
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        if (request !== this.request) return;
        this.setSaving(editor, false);
        input.value = '';
        this.alertService.success(editor.setting.SettingName + ' uploaded successfully.');
        this.load();
      },
      error: error => {
        if (request !== this.request) return;
        this.setSaving(editor, false);
        input.value = '';
        this.alertService.error(Utils.getAjaxError(error));
      }
    });
  }
}
